import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Button } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CmsCardComponent } from '../cms-card/cms-card';
import { CmsApiService, CmsItem } from '../cms-api/cms-api.service';
const ALL_VALUE = '__all__';
type CardService = CmsItem & { _cardId: number };
@Component({
  selector: 'lib-cms-services',
  standalone: true,
  imports: [CmsCardComponent, Button, SelectButton, FormsModule, CdkDropList, CdkDrag, Toast],
  providers: [MessageService],
  templateUrl: './cms-services.html',
  styleUrl: './cms-services.scss'
})
export class CmsServicesComponent implements OnInit {
  private readonly api = inject(CmsApiService);
  private readonly messageService = inject(MessageService);
  private nextId = 0;
  readonly services = signal<CardService[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly allLabels = computed<string[]>(() => {
    const set = new Set<string>();
    this.services().forEach((s: CardService) => s.labels.forEach((l: string) => set.add(l)));
    return Array.from(set).sort();
  });
  readonly labelOptions = computed<{ label: string; value: string }[]>(() => [
    { label: 'All', value: ALL_VALUE },
    ...this.allLabels().map((l: string) => ({ label: l, value: l }))
  ]);
  readonly selectedLabels = signal<string[]>([]);
  readonly displayedSelection = computed<string[]>(() =>
    this.selectedLabels().length === 0 ? [ALL_VALUE] : this.selectedLabels()
  );
  readonly filteredServices = computed<CardService[]>(() => {
    const selected = this.selectedLabels();
    if (!selected.length) return this.services();
    return this.services().filter((s: CardService) => s.labels.some((l: string) => selected.includes(l)));
  });
  readonly hasInvalidCards = computed<boolean>(() =>
    this.services().some((s: CardService) => !s.title.trim() || !s.description.trim() || !s.image || s.labels.length === 0)
  );

  ngOnInit(): void {
    this.api.getServices().subscribe({
      next: (items: CmsItem[]) => {
        this.services.set(items.map((item: CmsItem) => ({ ...item, _cardId: this.nextId++ })));
        this.loading.set(false);
      },
      error: (err: unknown) => {
        console.error('[CmsServices] Failed to load', err);
        this.error.set('Failed to load services from server.');
        this.loading.set(false);
      }
    });
  }

  onAddService(): void {
    this.services.update((list: CardService[]) => [
      { title: '', description: '', image: '', labels: [], _cardId: this.nextId++ },
      ...list
    ]);
  }

  onServiceChange(cardId: number, updated: CmsItem): void {
    this.services.update((list: CardService[]) =>
      list.map((item: CardService) => item._cardId === cardId ? { ...updated, _cardId: item._cardId } : item)
    );
  }

  onDeleteService(cardId: number): void {
    const item = this.services().find((s: CardService) => s._cardId === cardId);
    if (item?.id != null) {
      this.api.deleteService(item.id).subscribe({
        error: (err: unknown) => console.error('[CmsServices] Delete failed', err)
      });
    }
    this.services.update((list: CardService[]) => list.filter((s: CardService) => s._cardId !== cardId));
  }

  onDrop(event: CdkDragDrop<CardService[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const visibleIds = this.filteredServices().map((item: CardService) => item._cardId);
    this.services.update((list: CardService[]) => {
      if (visibleIds.length === list.length) {
        const next = [...list];
        moveItemInArray(next, event.previousIndex, event.currentIndex);
        return next;
      }
      const reordered = list.filter((item: CardService) => visibleIds.includes(item._cardId));
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      const queue = [...reordered];
      return list.map((item: CardService) => visibleIds.includes(item._cardId) ? (queue.shift() ?? item) : item);
    });
  }

  onLabelFilterChange(values: string[]): void {
    const vals = values ?? [];
    if (!vals.length) { this.selectedLabels.set([]); return; }
    if (vals.includes(ALL_VALUE) && this.selectedLabels().length > 0) { this.selectedLabels.set([]); return; }
    const real = vals.filter((v: string) => v !== ALL_VALUE);
    this.selectedLabels.set(real.length ? real : []);
  }

  onPublish(): void {
    if (this.hasInvalidCards()) return;
    this.saving.set(true);
    const payload = this.services().map(({ _cardId, ...rest }: CardService) => rest);
    this.api.publishServices(payload).subscribe({
      next: (saved: CmsItem[]) => {
        this.services.set(saved.map((item: CmsItem) => ({ ...item, _cardId: this.nextId++ })));
        this.saving.set(false);
        this.messageService.add({ severity: 'success', summary: 'Published', detail: 'Services published successfully.' });
      },
      error: (err: unknown) => {
        console.error('[CmsServices] Publish failed', err);
        this.saving.set(false);
        this.messageService.add({ severity: 'error', summary: 'Publish failed', detail: 'Could not publish services. Please try again.' });
      }
    });
  }
}
